import { Loader, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import FileDropZone from '~/components/FileDropZone'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import UploadProgress from '~/components/UploadProgress'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useModuleImport } from '~/pages/backoffice/admin/modules/use-module-import'
import { formatBytes } from '~/utils/bytes'

type Props = {
  /** Rafraîchit la liste et le quota. */
  onImported: () => Promise<unknown>
  /** Poids maximal d'un package, borne de l'API ; inconnu tant que le quota n'est pas chargé. */
  maxUploadBytes?: number | null
}

/**
 * Le formulaire d'import d'un package SCORM (#310) : un titre, une zone où
 * déposer le zip, puis la progression de l'envoi et du traitement.
 */
function ModuleImportCard({ onImported, maxUploadBytes }: Props) {
  const { t, i18n } = useTranslation()
  const { title, setTitle, file, chooseFile, error, state, submit, cancel, busy } = useModuleImport(onImported)

  return (
    <Card {...tourAnchor('modules.import')}>
      <CardHeader>
        <CardTitle>{t('modules.import.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium" htmlFor="module-title">{t('common.title')}</label>
          <Input
            id="module-title"
            value={title}
            disabled={busy}
            onChange={e => setTitle(e.target.value)}
            placeholder={t('modules.titlePlaceholder')}
          />
        </div>
        <div className="flex flex-col gap-1.5" {...tourAnchor('modules.dropzone')}>
          <label className="text-sm font-medium" htmlFor="module-file">{t('modules.package')}</label>
          <FileDropZone
            id="module-file"
            rule={{ extensions: ['.zip'], maxBytes: maxUploadBytes }}
            file={file}
            onFile={chooseFile}
            disabled={busy}
            hint={maxUploadBytes
              ? t('modules.upload.hint', { max: formatBytes(maxUploadBytes, i18n.language) })
              : t('modules.upload.hintNoMax')}
          />
        </div>

        <UploadProgress state={state} processingLabel={t('modules.upload.processing')} onCancel={cancel} />

        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

        <Button className="self-end" onClick={() => void submit()} disabled={busy}>
          {busy ? <Loader className="mr-2 size-4 animate-spin" /> : <Upload className="mr-2 size-4" />}
          {t('common.import')}
        </Button>
      </CardContent>
    </Card>
  )
}

export default ModuleImportCard
